import AWS from 'aws-sdk/global'
import changeCase from 'change-case'
import Vue from 'vue'
import 'aws-sdk/clients/cloudwatchevents'
import 'aws-sdk/clients/cloudwatchlogs'

function ensureAuth () {
  return new Promise((resolve, reject) => {
    if (AWS.config.credentials.expired) {
      AWS.config.credentials.refresh(() => resolve())
    } else {
      resolve()
    }
  })
}

function mockCORS (api, task) {
  return function (params, callback) {
    const call = ensureAuth().then(() => {
      return Vue.http.post('https://afgbfrzgw9.execute-api.us-east-1.amazonaws.com/prod/raptorJobsMicroservice', {
        api: api,
        task: task,
        params: params,
        credentials: {
          accessKeyId: AWS.config.credentials.accessKeyId,
          secretAccessKey: AWS.config.credentials.secretAccessKey,
          sessionToken: AWS.config.credentials.sessionToken
        }
      }).then(function (response) {
        return response.body
      })
    })

    if (typeof callback === 'function') {
      call.then(callback)
    } else {
      return {
        promise: () => call
      }
    }
  }
}

AWS.Batch = function () {
  this.describeJobDefinitions = mockCORS('Batch', 'describeJobDefinitions')
  this.listJobs = mockCORS('Batch', 'listJobs')
  this.describeJobs = mockCORS('Batch', 'describeJobs')
  this.submitJob = mockCORS('Batch', 'submitJob')
}

export class RaptorJobs {
  constructor () {
    Vue.$log.debug('Creating APIs')

    this._logsApi = new AWS.CloudWatchLogs({
      region: 'us-east-1'
    })

    this._eventsApi = new AWS.CloudWatchEvents({
      region: 'us-east-1'
    })

    this._batchApi = new AWS.Batch({
      region: 'us-east-1'
    })
  }

  runJob (job) {
    return this._batchApi.submitJob({
      'jobDefinition': job.job,
      'jobName': job.name + '-' + Date.now().toString(),
      'containerOverrides': {
        'environment': job.environment
      },
      'parameters': job.parameters,
      'jobQueue': job.queue
    }).promise()
  }

  getRules (targets, queues) {
    return Promise
      .all([this._eventRules(targets), this._batchJobs(queues)])
      .then(values => {
        const jobs = { }
        const history = values[1].reduce((map, job) => {
          const name = job.jobName.split('-').shift()

          job.time = parseInt(job.jobName.split('-').pop())

          if (!(name in map) || job.time > map[name].time) {
            map[name] = job
          }

          return map
        }, { })

        values[0].forEach((rule) => {
          rule[1].Targets.forEach((target) => {
            const configuration = JSON.parse(target.Input)
            let name = changeCase.titleCase(configuration.name).split(' ').slice(1)
            const environment = name.shift()

            name = name.join(' ')

            configuration.name.replace(/([A-Z][A-Z]+)[A-Z]/, function (match, acronym) {
              name = name.replace(changeCase.titleCase(acronym), acronym)

              return match
            })

            if (!(name in jobs)) {
              jobs[name] = {
                'name': name,
                'description': rule[0].Description,
                'arn': rule[0].Arn,
                'cron': rule[0].ScheduleExpression,
                'environments': { }
              }
            }

            jobs[name].environments[changeCase.lower(environment)] = configuration
            configuration.target = target.Arn
            configuration.enabled = rule[0].State === 'ENABLED'
            configuration.ruleName = rule[0].Name

            if (configuration.name in history) {
              configuration.history = history[configuration.name]
            }
          })
        })

        return Object.values(jobs)
      })
  }

  getLogs (jobId, group) {
    return this._batchApi.describeJobs({
      'jobs': [jobId]
    }).promise().then((response) => {
      return this._logsApi.getLogEvents({
        logGroupName: group,
        logStreamName: response.jobs[0].container.logStreamName,
        startFromHead: true,
        limit: 1000
      }).promise().then((logs) => {
        let title = response.jobs[0].jobName.split('-').shift()
        let name = changeCase.titleCase(title).split(' ').slice(1)

        name = name.join(' ')

        title.replace(/([A-Z][A-Z]+)[A-Z]/, function (match, acronym) {
          name = name.replace(changeCase.titleCase(acronym), acronym)

          return match
        })

        return {
          logs: logs.events,
          name: name,
          stream: response.jobs[0].container.logStreamName,
          started: response.jobs[0].startedAt
        }
      })
    })
  }

  enableSchedule (job) {
    if (job.enabled) {
      return this._eventsApi.disableRule({
        'Name': job.ruleName
      }).promise()
    } else {
      return this._eventsApi.enableRule({
        'Name': job.ruleName
      }).promise()
    }
  }

  _eventRules (targets, nextToken, rules = []) {
    return Promise.all(targets.map((target) => this._eventTargetRules(target))).then((rules) => {
      return Array.concat.apply([], rules)
    })
  }

  _eventTargetRules (target, nextToken, rules = []) {
    const params = {
      TargetArn: target
    }

    if (nextToken) {
      params.NextToken = nextToken
    }

    return this._eventsApi.listRuleNamesByTarget(params).promise()
      .then((response) => {
        rules.push.apply(rules, response.RuleNames.map(name => this._ruleTargets(name)))

        if (response.NextToken) {
          return this._eventTargetRules(target, response.NextToken, rules)
        } else {
          return Promise.all(rules)
        }
      })
  }

  _ruleTargets (ruleName) {
    return Promise.all([
      this._eventsApi.describeRule({ Name: ruleName }).promise(),
      this._eventsApi.listTargetsByRule({ Rule: ruleName }).promise()
    ])
  }

  _batchJobs (queues) {
    return Promise.all(queues.map((queue) => {
      return Promise.all([
        this._batchJobsStatus('SUBMITTED', queue),
        this._batchJobsStatus('PENDING', queue),
        this._batchJobsStatus('RUNNABLE', queue),
        this._batchJobsStatus('STARTING', queue),
        this._batchJobsStatus('RUNNING', queue),
        this._batchJobsStatus('SUCCEEDED', queue),
        this._batchJobsStatus('FAILED', queue)
      ])
    })).then((jobs) => Array.concat.apply([], jobs.map((list) => Array.concat.apply([], list))))
  }

  _batchJobsStatus (status, queue, nextToken, jobs = []) {
    const params = {
      'jobQueue': queue,
      'jobStatus': status,
      'maxResults': 100
    }
    let responseToken

    if (nextToken) {
      params.nextToken = nextToken
    }

    return this._batchApi.listJobs(params).promise()
      .then((response) => {
        responseToken = response.nextToken

        if (response.jobSummaryList.length) {
          return this._batchApi.describeJobs({
            jobs: response.jobSummaryList.map(job => job.jobId)
          }).promise()
        } else {
          return { body: { jobs: [] } }
        }
      })
      .then((response) => {
        jobs.push.apply(jobs, response.jobs)

        if (responseToken) {
          return this._batchJobsStatus(status, queue, responseToken, jobs)
        } else {
          return jobs
        }
      })
  }
}



// WEBPACK FOOTER //
// ./src/services/raptor-jobs.js